import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import Button from "../common/Button";
import Modal from "../common/Modal";
import SubjectForm from "../admin/SubjectForm";
import {
  getLatestSubjectId,
  getAllSubjects,
  createSubject,
} from "../../services/subjectService";
import TeacherPicker from "../common/TeacherPicker";
import { getAllCourses } from "../../services/courseService";

// Derive a lightweight course code such as CSC123 from the name
const generateCourseCode = (courseName = "") => {
  const trimmed = courseName.replace(/[^a-zA-Z]/g, "").toUpperCase();
  const prefix = trimmed.slice(0, 3);
  if (!prefix) return "";
  const hash = Array.from(courseName).reduce(
    (acc, char, index) => acc + char.charCodeAt(0) * (index + 1),
    0
  );
  const numericPart = String(hash % 1000).padStart(3, "0");
  return `${prefix}${numericPart}`;
};

const CourseForm = ({
  onSubmit,
  onCancel,
  loading,
  initialData = {},
  step,
}) => {
  const {
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: initialData,
  });

  const [selectedSubjects, setSelectedSubjects] = useState([]);
  const [subjectsError, setSubjectsError] = useState("");
  const [showSubjectModal, setShowSubjectModal] = useState(false);
  const [availableSubjects, setAvailableSubjects] = useState([]);
  const [loadingAvailableSubjects, setLoadingAvailableSubjects] =
    useState(false);
  const [creatingSubjectLocal, setCreatingSubjectLocal] = useState(false);
  const [codeManuallyEdited, setCodeManuallyEdited] = useState(
    Boolean(initialData?.code)
  );
  const [autoGeneratedCode, setAutoGeneratedCode] = useState("");
  const courseName = watch("name");

  // Auto-fill the Subject ID with the latest from backend if not provided
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (initialData && initialData.subjectId) return;
      try {
        const latest = await getLatestSubjectId();
        // If backend returns the latest existing SubjectID, show the next available id (latest + 1)
        if (!cancelled && typeof latest === "number" && !Number.isNaN(latest)) {
          const nextId = latest + 1;
          setValue("subjectId", String(nextId), { shouldValidate: true });
        }
      } catch (_) {
        // ignore; user can type manually
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [initialData, setValue]);

  // Initialize selectedSubjects from initialData if present
  useEffect(() => {
    const list = [];
    if (Array.isArray(initialData?.subjects) && initialData.subjects.length) {
      for (const s of initialData.subjects) {
        if (typeof s === "string") {
          list.push({ id: null, name: s, isNew: false });
        } else if (s && typeof s === "object") {
          list.push({
            id: s.id ?? null,
            name: s.name ?? s.subjectName ?? s.SubjectName ?? "",
            isNew: false,
          });
        }
      }
    } else if (initialData?.subject) {
      list.push({
        id: initialData.subjectId ?? null,
        name: initialData.subject,
        isNew: false,
      });
    } else if (initialData?.subjectId && initialData?.subjectName) {
      list.push({
        id: initialData.subjectId,
        name: initialData.subjectName,
        isNew: false,
      });
    }
    setSelectedSubjects(list);
  }, [initialData]);

  const loadAvailableSubjects = async () => {
    setLoadingAvailableSubjects(true);
    try {
      const subs = await getAllSubjects();
      setAvailableSubjects(subs || []);
    } catch (err) {
      console.error("Failed to load subjects for picker", err);
      setAvailableSubjects([]);
    } finally {
      setLoadingAvailableSubjects(false);
    }
  };

  useEffect(() => {
    // pre-load subjects so picker is responsive
    loadAvailableSubjects();
  }, []);

  useEffect(() => {
    if (!Array.isArray(availableSubjects) || !availableSubjects.length) {
      return;
    }

    setSelectedSubjects((prev) => {
      if (!Array.isArray(prev) || !prev.length) {
        return prev;
      }

      const normalize = (value) =>
        String(value ?? "")
          .trim()
          .toLowerCase();

      const lookup = new Map();
      for (const subject of availableSubjects) {
        const key = normalize(
          subject?.name ??
            subject?.subjectName ??
            subject?.SubjectName ??
            subject?.title ??
            subject?.Title
        );
        if (!key || lookup.has(key)) continue;
        const idCandidate =
          subject?.id ??
          subject?.SubjectID ??
          subject?.subjectId ??
          subject?.subjectID ??
          null;
        lookup.set(key, {
          id: idCandidate,
          name:
            subject?.name ??
            subject?.subjectName ??
            subject?.SubjectName ??
            subject?.title ??
            subject?.Title ??
            "",
        });
      }

      const primaryId =
        initialData?.subjectId ??
        initialData?.SubjectID ??
        initialData?.subjectID ??
        null;

      let changed = false;
      const next = prev.map((entry, index) => {
        if (!entry || entry.id) return entry;

        const key = normalize(entry.name);
        if (key && lookup.has(key)) {
          const match = lookup.get(key);
          if (match?.id !== undefined && match?.id !== null) {
            changed = true;
            return { ...entry, id: match.id };
          }
        }

        if (index === 0 && primaryId !== null && primaryId !== undefined) {
          changed = true;
          return { ...entry, id: primaryId };
        }

        return entry;
      });

      return changed ? next : prev;
    });
  }, [
    availableSubjects,
    initialData?.subjectId,
    initialData?.SubjectID,
    initialData?.subjectID,
  ]);

  useEffect(() => {
    if (initialData?.code) {
      setCodeManuallyEdited(true);
    }
  }, [initialData?.code]);

  useEffect(() => {
    if (initialData?.code || codeManuallyEdited) return;
    if (!courseName) {
      if (autoGeneratedCode) {
        setAutoGeneratedCode("");
        setValue("code", "", { shouldDirty: true, shouldValidate: true });
      }
      return;
    }

    const generated = generateCourseCode(courseName);
    if (!generated || generated === autoGeneratedCode) return;

    setAutoGeneratedCode(generated);
    setValue("code", generated, { shouldDirty: true, shouldValidate: true });
  }, [
    autoGeneratedCode,
    codeManuallyEdited,
    courseName,
    initialData?.code,
    setValue,
  ]);

  // Async validator to prevent duplicate course codes
  const validateCourseCode = async (val) => {
    try {
      const trimmed = String(val || "")
        .trim()
        .toUpperCase();
      if (!trimmed) return true;
      const existing =
        initialData && (initialData.code || initialData.CourseCode)
          ? String(initialData.code || initialData.CourseCode || "")
              .trim()
              .toUpperCase()
          : null;
      if (existing && existing === trimmed) return true;
      const courses = await getAllCourses();
      const found = (courses || []).find(
        (c) =>
          String(c.code || c.CourseCode || c.courseCode || "")
            .toString()
            .trim()
            .toUpperCase() === trimmed
      );
      if (!found) return true;
      const foundId = found.id ?? found.CourseID ?? found.courseId ?? null;
      const currentId =
        initialData?.id ??
        initialData?.CourseID ??
        initialData?.courseId ??
        null;
      if (currentId && String(foundId) === String(currentId)) return true;
      return "Course code already in use";
    } catch (err) {
      // On API failure, allow submit (do not block UX)
      return true;
    }
  };

  const codeField = register("code", {
    required: "Course code is required",
    pattern: {
      value: /^[A-Za-z0-9_-]{2,20}$/,
      message: "Course code must be 2–20 alphanumeric characters (no spaces)",
    },
    // async uniqueness validator
    validate: validateCourseCode,
  });

  return (
    <form
      onSubmit={handleSubmit((data) => {
        setSubjectsError("");
        // require at least one subject on course creation
        if (!selectedSubjects || selectedSubjects.length === 0) {
          setSubjectsError(
            "At least one class must be selected for the course."
          );
          return;
        }

        // Determine teacher id (prefer form field, fallback to empty)
        const rawTeacher =
          data.teacherId ?? data.teacherID ?? data.TeacherID ?? "";
        const teacherId =
          rawTeacher === null || rawTeacher === undefined ? "" : rawTeacher;

        // Prefer first selected subject as the primary SubjectID
        const firstSub =
          selectedSubjects && selectedSubjects.length
            ? selectedSubjects[0]
            : null;
        const subjId = firstSub
          ? firstSub.id ?? data.subjectId ?? data.SubjectID ?? null
          : data.subjectId ?? data.SubjectID ?? null;

        // Build SubjectIDs array (only IDs that are available)
        const subjectIds = (selectedSubjects || [])
          .map((s) => s?.id ?? s?.SubjectID ?? s?.subjectId ?? null)
          .filter((v) => v !== null && v !== undefined)
          .map((v) => (typeof v === "string" ? Number(v) : v));

        const payload = {
          ...data,
          subjects: selectedSubjects,
          // ensure both shapes are present for backend compatibility
          TeacherID: teacherId,
          teacherId: teacherId,
          SubjectID: subjId,
          subjectId: subjId,
          // new API expects SubjectIDs array
          SubjectIDs: subjectIds,
        };

        onSubmit(payload);
      })}
      className="space-y-6"
    >
      {/* Step indicator removed per request - show the form directly */}
      <div>
        <label
          htmlFor="code"
          className="block text-sm font-medium text-gray-700 dark:text-gray-300"
        >
          Course Code
        </label>
        <input
          disabled
          id="code"
          name="code"
          type="text"
          {...codeField}
          onChange={(e) => {
            // normalize to uppercase and disallow spaces
            const upper = String(e.target.value || "")
              .toUpperCase()
              .replace(/\s+/g, "");
            setCodeManuallyEdited(true);
            // synthesize a small event-like object for react-hook-form
            codeField.onChange({ target: { name: "code", value: upper } });
          }}
          // validate uniqueness on blur to avoid extra calls on every keystroke
          onBlur={async (e) => {
            const v = String(e.target.value || "").trim();
            if (!v) return;
            const res = await validateCourseCode(v);
            if (res !== true) {
              // set form error for code field
              try {
                // access internal react-hook-form set error via register return if available
                // fallback: throw to show existing error UI (errors.code)
                // we rely on the existing errors rendering which will reflect validation result
              } catch (err) {
                // noop
              }
            }
          }}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
        />
        {errors.code && (
          <p className="mt-1 text-sm text-red-600">{errors.code.message}</p>
        )}
      </div>

      <div>
        <label
          htmlFor="name"
          className="block text-sm font-medium text-gray-700 dark:text-gray-300"
        >
          Course Name
        </label>
        <input
          id="name"
          name="name"
          type="text"
          {...register("name", { required: "Course name is required" })}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
        />
        {errors.name && (
          <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          Assigned Teacher
        </label>
        <Controller
          name="teacherId"
          control={control}
          render={({ field }) => (
            <TeacherPicker
              value={field.value}
              onChange={(val) => field.onChange(val ?? "")}
              onBlur={field.onBlur}
              disabled={loading}
              showRefresh={false}
            />
          )}
        />
        {errors.teacherId && (
          <p className="mt-1 text-sm text-red-600">
            {errors.teacherId.message}
          </p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          Classes
        </label>
        <div className="mt-2 space-y-2">
          {selectedSubjects && selectedSubjects.length ? (
            <ul className="list-disc list-inside">
              {selectedSubjects.map((s, idx) => (
                <li
                  key={`${s.name}-${idx}`}
                  className="flex items-center justify-between gap-3"
                >
                  <div className="text-sm">{s.name}</div>
                  <div>
                    <button
                      type="button"
                      onClick={() =>
                        setSelectedSubjects((prev) =>
                          prev.filter((_, i) => i !== idx)
                        )
                      }
                      className="text-sm text-red-600 hover:underline"
                    >
                      Remove
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <div className="text-sm text-gray-500">No classes selected.</div>
          )}

          <div className="flex flex-col gap-2 pt-2 sm:flex-row sm:items-center">
            <div className="w-full sm:flex-1">
              <select
                onChange={(e) => {
                  const val = e.target.value;
                  if (!val) return;
                  const found = (availableSubjects || []).find(
                    (a) => String(a.id) === String(val)
                  );
                  if (found) {
                    const name =
                      found.name ??
                      found.subjectName ??
                      found.SubjectName ??
                      "";
                    // avoid duplicates by name
                    if (
                      !selectedSubjects.some(
                        (ss) =>
                          String(ss.name).toLowerCase() ===
                          String(name).toLowerCase()
                      )
                    ) {
                      setSelectedSubjects((prev) => [
                        ...prev,
                        { id: found.id ?? null, name, isNew: false },
                      ]);
                      // also set primary subject id field so API receives SubjectID
                      try {
                        setValue("subjectId", found.id ?? "", {
                          shouldDirty: true,
                          shouldValidate: true,
                        });
                      } catch (err) {
                        // ignore
                      }
                    }
                  }
                  // reset select
                  e.target.value = "";
                }}
                className="w-full rounded-md border border-gray-300 bg-white p-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                disabled={loadingAvailableSubjects}
              >
                <option value="">-- Choose existing class --</option>
                {(availableSubjects || []).map((s) => {
                  const name = s.name ?? s.subjectName ?? s.SubjectName ?? "";
                  return (
                    <option key={String(s.id)} value={String(s.id)}>
                      {name}
                    </option>
                  );
                })}
              </select>
            </div>

            <div className="sm:flex-shrink-0">
              <Button
                type="button"
                variant="secondary"
                onClick={() => setShowSubjectModal(true)}
                className="w-full justify-center sm:w-auto"
              >
                + New
              </Button>
            </div>
          </div>
          {subjectsError ? (
            <p className="mt-1 text-sm text-red-600">{subjectsError}</p>
          ) : null}
          {showSubjectModal && (
            <Modal
              isOpen={showSubjectModal}
              onClose={() => setShowSubjectModal(false)}
              title="Add new class"
              size="md"
            >
              <SubjectForm
                onSubmit={async (data) => {
                  // Persist the new subject immediately so we have an id to send with the course
                  setCreatingSubjectLocal(true);
                  try {
                    const payload = {
                      ...(data || {}),
                      ...(initialData?.name
                        ? { courseName: initialData.name }
                        : {}),
                    };
                    const created = await createSubject(payload);
                    const name =
                      created?.name ??
                      created?.subjectName ??
                      created?.SubjectName ??
                      data.name ??
                      "";
                    const id =
                      created?.id ??
                      created?.SubjectID ??
                      created?.subjectId ??
                      null;
                    setSelectedSubjects((prev) => [
                      ...prev,
                      { id: id, name, isNew: false, draft: created },
                    ]);
                    if (id !== null && id !== undefined) {
                      try {
                        setValue("subjectId", id, {
                          shouldDirty: true,
                          shouldValidate: true,
                        });
                      } catch (e) {
                        // ignore
                      }
                    }
                    setShowSubjectModal(false);
                  } catch (err) {
                    console.error("Failed to create subject inline:", err);
                  } finally {
                    setCreatingSubjectLocal(false);
                  }
                }}
                onCancel={() => setShowSubjectModal(false)}
                initial={{ courseName: initialData?.name }}
              />
            </Modal>
          )}
        </div>
      </div>

      <div>
        <label
          htmlFor="academicYear"
          className="block text-sm font-medium text-gray-700 dark:text-gray-300"
        >
          Academic Year
        </label>
        <input
          id="academicYear"
          name="academicYear"
          type="text"
          {...register("academicYear", {
            required: "Academic year is required",
          })}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
        />
        {errors.academicYear && (
          <p className="mt-1 text-sm text-red-600">
            {errors.academicYear.message}
          </p>
        )}
      </div>

      <div>
        <label
          htmlFor="description"
          className="block text-sm font-medium text-gray-700 dark:text-gray-300"
        >
          Description
        </label>
        <textarea
          id="description"
          name="description"
          rows={3}
          {...register("description")}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
        />
      </div>

      <div className="flex flex-col gap-2 sm:flex-row sm:justify-end sm:gap-3">
        {onCancel ? (
          <Button
            type="button"
            variant="secondary"
            onClick={onCancel}
            disabled={loading}
            className="w-full justify-center sm:w-auto"
          >
            Cancel
          </Button>
        ) : null}
        <Button
          type="submit"
          variant="primary"
          disabled={loading}
          className="w-full justify-center sm:w-auto"
        >
          {loading ? "Saving..." : "Save Course"}
        </Button>
      </div>
    </form>
  );
};

export default CourseForm;
